import { ApiError, serverMessage } from '@ghp/api-client'
import { appointmentSlot, inspectionTitle, newUuidV7 } from '@ghp/domain'
import { useToast } from '@ghp/ui'
import { useQueryClient } from '@tanstack/react-query'

import { useConnectionStatus } from '../../app/connection'
import type { FieldContext } from '../../app/routes/root'
import { useTeamData } from '../../app/use-team-data'
import type { AuthStore } from '../auth/auth-store'
import { showFieldAppointment } from '../../offline/data-db'
import { INQUIRIES_QUERY_KEY } from '../../offline/query-keys'
import type { AppointmentMove } from './components/OpeningSheet'

/** Pourquoi le geste est grisé — la seule condition, dite en une phrase. */
const OFFLINE_REASON =
  'Le déplacement part directement au bureau : il attend que la tablette soit en ligne.'

const PREVIOUS_QUEUED =
  'Le rendez-vous précédent n’est pas encore parti au bureau : réessayez dans un instant.'

/**
 * **Déplacer** un rendez-vous déjà pris, **en ligne seulement** — une écriture directe, jamais par
 * l'outbox (D4) : le déplacement annule un créneau dont l'usager a reçu la confirmation, et un
 * rejeu tardif écraserait celui que le bureau aurait posé entre-temps.
 *
 * ⚠️ **Un rendez-vous encore en file pour ce contrôle bloque le déplacement** : parti après lui, il
 * le remplacerait au bureau, et la descente le remontrerait sur la tablette.
 */
export function useAppointmentMove({
  auth,
  planning,
  accessToken: currentAccessToken,
  requestSync,
}: Pick<FieldContext, 'planning' | 'accessToken' | 'requestSync'> & {
  auth: AuthStore
}): AppointmentMove {
  const data = useTeamData()
  const queryClient = useQueryClient()
  const toast = useToast()
  const connection = useConnectionStatus(auth)

  return {
    unavailable: connection === 'online' ? null : OFFLINE_REASON,
    save: async (inquiry, move) => {
      const accessToken = currentAccessToken()
      if (accessToken === null) throw new Error(OFFLINE_REASON)

      const queued = await data.appointments
        .where('inquiryId')
        .equals(inquiry.id)
        .filter((entry) => entry.state === 'pending' || entry.state === 'sending')
        .count()
      if (queued > 0) {
        void requestSync()
        throw new Error(PREVIOUS_QUEUED)
      }

      try {
        await planning.recordFieldAppointment(accessToken, {
          id: newUuidV7(),
          inquiryId: inquiry.id,
          // Le créneau affiché : le serveur refuse si le bureau l'a changé depuis la descente.
          replacing: inquiry.scheduledAt ?? null,
          ...move,
        })
      } catch (failure: unknown) {
        if (failure instanceof ApiError && failure.status === 401) auth.tokenRefused(accessToken)
        throw new Error(serverMessage(failure) ?? undefined, { cause: failure })
      }

      await showFieldAppointment(data, { inquiryId: inquiry.id, ...move })
      await queryClient.invalidateQueries({ queryKey: INQUIRIES_QUERY_KEY })

      const slot = appointmentSlot(move.scheduledAt)
      const when = slot === null ? move.scheduledAt : `${slot.day} · ${slot.time}`
      toast('Rendez-vous modifié', `${inspectionTitle(inquiry, { short: true })} · ${when}`)
    },
  }
}
